/**
 * AuthProvider — wraps the app with Firebase Auth state.
 *
 * - Subscribes to `onAuthStateChanged` from `firebase/auth`.
 * - Exposes `useUser()` and `useIdToken()` for consumers.
 * - Accepts `overrideUser` for tests/Storybook to short-circuit Firebase.
 * - Sign-in/sign-out helpers live alongside (`useSignIn`, `useSignOut`)
 *   so consumers don't reach into `firebase/auth` directly.
 */
import { QueryClientContext } from "@tanstack/react-query";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getFirebaseAuth } from "./firebase.js";
import type { User as FirebaseUser } from "firebase/auth";
import type { ReactNode } from "react";

export interface AppUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

interface AuthContextValue {
  user: AppUser | null;
  /** True while we're still waiting for the first onAuthStateChanged callback. */
  loading: boolean;
  /** Returns the Firebase ID token for the current user, or null if unauthed. */
  getIdToken: () => Promise<string | null>;
  /** Email + password sign-in. Resolves once Firebase fires the listener. */
  signInWithEmail: (email: string, password: string) => Promise<void>;
  /** Popup-based Google sign-in. */
  signInWithGoogle: () => Promise<void>;
  /** Sign out the current user and clear caches. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  /**
   * Override the user in tests / Storybook. When provided, Firebase is not
   * initialised and `onAuthStateChanged` is not subscribed to.
   */
  overrideUser?: AppUser | null;
}

function toAppUser(fb: FirebaseUser | null): AppUser | null {
  if (!fb) return null;
  return { uid: fb.uid, email: fb.email, displayName: fb.displayName };
}

export function AuthProvider({ children, overrideUser }: AuthProviderProps) {
  const isOverride = overrideUser !== undefined;
  const [user, setUser] = useState<AppUser | null>(isOverride ? overrideUser : null);
  const [loading, setLoading] = useState<boolean>(!isOverride);
  // Hold the Firebase user so getIdToken() can call user.getIdToken() directly.
  const firebaseUserRef = useRef<FirebaseUser | null>(null);
  // Read the QueryClient context directly (may be undefined in tests that
  // don't wrap with QueryClientProvider). We only need it on sign-out.
  const queryClient = useContext(QueryClientContext);

  useEffect(() => {
    if (isOverride) return;
    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      firebaseUserRef.current = fbUser;
      setUser(toAppUser(fbUser));
      setLoading(false);
    });
    return () => {
      unsub();
    };
  }, [isOverride]);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (isOverride) {
      return user ? "stub-id-token" : null;
    }
    const fb = firebaseUserRef.current;
    if (!fb) return null;
    return fb.getIdToken();
  }, [isOverride, user]);

  const signInWithEmail = useCallback(async (email: string, password: string): Promise<void> => {
    const auth = getFirebaseAuth();
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signInWithGoogle = useCallback(async (): Promise<void> => {
    const auth = getFirebaseAuth();
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  }, []);

  const signOutAndClear = useCallback(async (): Promise<void> => {
    const auth = getFirebaseAuth();
    await signOut(auth);
    // Clear TanStack Query caches so a different user doesn't see stale data.
    queryClient?.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      getIdToken,
      signInWithEmail,
      signInWithGoogle,
      signOut: signOutAndClear,
    }),
    [user, loading, getIdToken, signInWithEmail, signInWithGoogle, signOutAndClear],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export function useUser(): AppUser | null {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useUser must be used within <AuthProvider>");
  return ctx.user;
}

export function useIdToken(): () => Promise<string | null> {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useIdToken must be used within <AuthProvider>");
  return ctx.getIdToken;
}
